import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { availabilityObjToArray, formatTimeSlot } from '../utils/dateUtils';
import Input from '../components/ui/Input';
import ProgressBar from '../components/ProgressBar';
import Button from '../components/ui/Button';
import { Clock, Edit, BookOpen, Target, AlertCircle, Mail } from 'lucide-react';
import Footer from '../components/Footer';

/**
 * Confirmation page for users coming from /start (no header, minimal styling)
 * OPTIMIZED VERSION - reduces API calls and navigates faster
 * Supports both group classes and private tutoring trials
 */
const StartConfirmationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state
  const { returnState } = location.state || {};
  const { classType, availability, isTutoringTrial, urlParams } = returnState || {};

  // Goal data state - Initialize from URL params if available
  const [goalData, setGoalData] = useState(() => {
    const initialData = {
      goal: '',
      goal_level: '',
      exam_type: '',
      exam_level: '',
      exam_year_phase: '',
      cantonese_level: ''
    };
    
    // Pre-fill from URL parameters if they exist
    if (urlParams) {
      if (urlParams.exam) {
        initialData.goal = 'exam';
        initialData.exam_type = urlParams.exam;
      } else if (urlParams.goal) {
        initialData.goal = urlParams.goal;
      }
      
      if (urlParams.level) {
        initialData.goal_level = urlParams.level;
      }
    }
    
    return initialData;
  });
  
  // State for class type data
  const [classTypeData, setClassTypeData] = useState(null);
  const [loadingClassType, setLoadingClassType] = useState(false);
  
  // Redirect if accessing directly without state
  useEffect(() => {
    console.log('Availability check:', availability);
    if (!availability) {
      navigate('/start');
    }
  }, [availability, navigate]);

  // Load class type data
  useEffect(() => {
    const loadClassType = async () => {
      const classTypeId = sessionStorage.getItem('startClassTypeId');
      if (!classTypeId) return;
      
      try {
        setLoadingClassType(true);
        const types = await api.classTypes.getActive();
        const selectedType = types.find(type => type.$id === classTypeId);
        if (selectedType) {
          setClassTypeData(selectedType);
        }
      } catch (err) {
        console.error('Error loading class type:', err);
      } finally {
        setLoadingClassType(false);
      }
    };
    
    loadClassType();
  }, []);

  // Handle back to edit availability
  const handleEditAvailability = () => {
    const classTypeId = sessionStorage.getItem('startClassTypeId');
    
    if (classTypeId) {
      navigate(`/start/${classTypeId}`, {
        state: { availability }
      });
    } else {
      navigate('/start');
    }
  };
  
  // Form state
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });
  
  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Validate form
      if (!formData.name || !formData.email || !formData.phone) {
        setError('Please fill in all fields');
        return;
      }
      
      // Validate goal selection based on class type (only for regular classes)
      if (!isTutoringTrial) {
        if (classTypeData?.category === 'mandarin' && !goalData.goal) {
          setError('Please select your learning goal');
          return;
        }
        
        if (classTypeData?.category === 'cantonese' && !goalData.cantonese_level) {
          setError('Please select your Cantonese level');
          return;
        }
      }
      
      // Convert availability object to array
      const availabilityArray = availabilityObjToArray(availability);
      const classTypeId = sessionStorage.getItem('startClassTypeId');
      
      // Step 1: Submit availability with goal data (fire and continue - don't wait)
      const availabilityPromise = api.availability.submit(
        user.$id,
        classTypeId,
        availabilityArray,
        formData.name,
        formData.email,
        formData.phone,
        goalData,
        isTutoringTrial
      );

      // Step 2: Send confirmation emails (fire and continue - don't wait)
      const emailPromise = api.notifications.sendAvailabilityConfirmation(
        formData.name,
        formData.email,
        classType,
        availabilityArray,
        classTypeId,
        formData.phone,
        goalData,
        isTutoringTrial
      ).catch(emailError => {
        console.warn('Failed to send confirmation email:', emailError);
        // Don't fail the whole process if email fails
      });

      // Step 3: Navigate immediately to success page (don't wait for background tasks)
      console.log('Navigating to success page...');
      navigate('/start-success', {
        state: {
          classType,
          availability,
          userEmail: formData.email,
          goalData,
          isTutoringTrial // Pass flag to success page
        }
      });
      
      // Background tasks continue (availability submission and email)
      Promise.all([availabilityPromise, emailPromise]).then(() => {
        console.log('Background tasks completed successfully');
      }).catch(err => {
        console.warn('Some background tasks failed:', err);
        // User is already on success page, so this doesn't affect UX
      });

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle back button
  const handleBack = () => {
    const classTypeId = sessionStorage.getItem('startClassTypeId');
    
    if (classTypeId) {
      navigate(`/start/${classTypeId}`, {
        state: { availability }
      });
    } else {
      navigate('/start');
    }
  };
  
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Main content */}
      <main className="container mx-auto py-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Progress bar */}
          <ProgressBar currentStep={2} />
          
          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">
              Submit Your Availability
            </h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>
          
          {error && (
            <div className="p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg mb-6">
              <p className="text-red-400">{error}</p>
            </div>
          )}
          
          {/* Show class type info */}
          {classTypeData && (
            <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-4 py-3 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base flex items-center">
                    <BookOpen size={16} className="mr-2" />
                    Your Selected {isTutoringTrial ? 'Trial Lesson' : 'Class'}
                  </h3>
                </div>
              </div>
              <div className="p-4">
                <p className="text-gray-300 text-sm">{classTypeData.name}</p>
              </div>
            </div>
          )}
          
          {/* Show selected times */}
          {availability && availabilityObjToArray(availability).length > 0 && (
            <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-4 py-3 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base flex items-center">
                    <Clock size={16} className="mr-2" />
                    Your Selected Times
                  </h3>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleEditAvailability}
                    className="text-purple-400 hover:text-purple-300 flex items-center text-sm"
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
              
              <div className="p-4">
                <div className="space-y-4">
                  {(() => {
                    // Parse and sort slots
                    const slots = availabilityObjToArray(availability);
                    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                    
                    // Parse slot strings into objects
                    const parsedSlots = slots.map(slotStr => {
                      const [day, time] = slotStr.split('-');
                      return { day: day.trim(), time: time.trim(), original: slotStr };
                    });
                    
                    // Group by day
                    const slotsByDay = parsedSlots.reduce((acc, slot) => {
                      if (!acc[slot.day]) {
                        acc[slot.day] = [];
                      }
                      acc[slot.day].push(slot);
                      return acc;
                    }, {});
                    
                    // Sort each day's slots by time
                    Object.keys(slotsByDay).forEach(day => {
                      slotsByDay[day].sort((a, b) => {
                        // Convert to 24-hour format for comparison
                        const convertTo24Hour = (timeStr) => {
                          const [time, period] = timeStr.split(' ');
                          let [hours, minutes] = time.split(':').map(Number);
                          
                          if (period === 'PM' && hours !== 12) {
                            hours += 12;
                          } else if (period === 'AM' && hours === 12) {
                            hours = 0;
                          }
                          
                          return hours * 60 + minutes;
                        };
                        
                        return convertTo24Hour(a.time) - convertTo24Hour(b.time);
                      });
                    });
                    
                    // Helper function to format time range
                    const formatTimeRange = (timeStr) => {
                      const [time, period] = timeStr.split(' ');
                      let [hours, minutes] = time.split(':').map(Number);
                      
                      // Convert to 24-hour for calculation
                      let hours24 = hours;
                      if (period === 'PM' && hours !== 12) {
                        hours24 += 12;
                      } else if (period === 'AM' && hours === 12) {
                        hours24 = 0;
                      }
                      
                      // Calculate end time (30 minutes later)
                      let endMinutes = minutes + 30;
                      let endHours24 = hours24;

                      if (endMinutes >= 60) {
                        endMinutes -= 60;
                        endHours24 += 1;
                      }

                      let endHours = endHours24 % 12 || 12;
                      let endPeriod = endHours24 >= 12 ? 'PM' : 'AM';

                      return `${timeStr} - ${endHours}:${endMinutes.toString().padStart(2, '0')} ${endPeriod}`;
                    };
                    
                    // Render by day in order
                    return dayOrder.map(day => {
                      if (!slotsByDay[day] || slotsByDay[day].length === 0) return null;
                      
                      return (
                        <div key={day}>
                          <h4 className="text-sm font-semibold text-white mb-2">{day}</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 ml-2">
                            {slotsByDay[day].map((slot, index) => (
                              <div key={index} className="flex items-center text-sm">
                                <span className="text-purple-400 mr-2">•</span>
                                <span className="text-gray-300">{formatTimeRange(slot.time)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }).filter(Boolean);
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Goals Section - Only show for regular classes */}
          {!isTutoringTrial && !loadingClassType && classType && (() => {
            // Check if it's a Mandarin class
            const isMandarin = classType.name?.toLowerCase() == 'mandarin';              
            
            // Check if it's a Cantonese class
            const isCantonese = classType.name?.toLowerCase() == 'cantonese';

            // Only render the card if it's Mandarin or Cantonese
            if (!isMandarin && !isCantonese) {
              return null;
            }

            return (
              <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-4 py-3 border-b border-gray-700">
                  <h3 className="font-semibold text-base flex items-center">
                    <Target size={16} className="mr-2" />
                    What's your goal?
                  </h3>
                </div>
                <div className="p-4 space-y-3">
                  {isMandarin && (
                    <>
                      {[
                        { value: 'hsk', label: 'HSK Test Preparation' },
                        { value: 'exam', label: 'School Exam Support' },
                        { value: 'conversational', label: 'Light Conversational (no homework)' }
                      ].map((option) => (
                        <div key={option.value}>
                          <label
                            className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                              goalData.goal === option.value
                                ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                                : 'border-gray-600 hover:border-gray-500'
                            }`}
                          >
                            <input
                              type="radio"
                              name="goal"
                              value={option.value}
                              checked={goalData.goal === option.value}
                              onChange={(e) => setGoalData(prev => ({ 
                                ...prev, 
                                goal: e.target.value,
                                goal_level: '',
                                exam_type: '',
                                exam_level: '',
                                exam_year_phase: ''
                              }))}
                              className="sr-only"
                            />
                            <span className="flex-1 text-sm font-medium">{option.label}</span>
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              goalData.goal === option.value ? 'border-purple-500' : 'border-gray-500'
                            }`}>
                              {goalData.goal === option.value && (
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                              )}
                            </div>
                          </label>

                          {/* HSK Level Selection */}
                          {goalData.goal === 'hsk' && option.value === 'hsk' && (
                            <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                              <label className="block text-sm font-medium text-gray-300 mb-3">
                                Which HSK level are you targeting?
                              </label>
                              <div className="grid grid-cols-3 gap-2">
                                {[1, 2, 3, 4, 5, 6].map((level) => (
                                  <button
                                    key={level}
                                    type="button"
                                    onClick={() => setGoalData(prev => ({ ...prev, goal_level: level.toString() }))}
                                    className={`p-2 rounded-md text-sm font-medium transition-all ${
                                      goalData.goal_level === level.toString()
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                    }`}
                                  >
                                    HSK {level}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Conversational Level Selection */}
                          {goalData.goal === 'conversational' && option.value === 'conversational' && (
                            <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                              <label className="block text-sm font-medium text-gray-300 mb-3">
                                What level are you?
                              </label>
                              <div className="grid grid-cols-3 gap-2">
                                {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                                  <button
                                    key={level}
                                    type="button"
                                    onClick={() => setGoalData(prev => ({ ...prev, goal_level: level }))}
                                    className={`p-2 rounded-md text-sm font-medium transition-all ${
                                      goalData.goal_level === level
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                    }`}
                                  >
                                    {level}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Exam Details */}
                          {goalData.goal === 'exam' && option.value === 'exam' && (
                            <div className="mt-3 ml-4 space-y-4">
                              <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                  Which exam are you preparing for?
                                </label>
                                <select
                                  value={goalData.exam_type}
                                  onChange={(e) => setGoalData(prev => ({
                                    ...prev,
                                    exam_type: e.target.value,
                                    exam_level: '',
                                    exam_year_phase: ''
                                  }))}
                                  className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white text-sm"
                                >
                                  <option value="">Select exam...</option>
                                  {['IGCSE 0523', 'IGCSE 0547', 'IB', 'A-Level', 'DSE'].map((exam) => (
                                    <option key={exam} value={exam}>{exam}</option>
                                  ))}
                                </select>
                              </div>

                              {goalData.exam_type === 'IB' && (
                                <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                                  <label className="block text-sm font-medium text-gray-300 mb-3">
                                    IB Level
                                  </label>
                                  <div className="grid grid-cols-2 gap-2">
                                    {['IB SL', 'IB HL'].map((level) => (
                                      <button
                                        key={level}
                                        type="button"
                                        onClick={() => setGoalData(prev => ({ ...prev, exam_level: level }))}
                                        className={`p-2 rounded-md text-sm font-medium transition-all ${
                                          goalData.exam_level === level
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                        }`}
                                      >
                                        {level}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {goalData.exam_type && (goalData.exam_type !== 'IB' || goalData.exam_level) && (
                                <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                                  <label className="block text-sm font-medium text-gray-300 mb-3">
                                    Year/Phase
                                  </label>
                                  <div className="grid grid-cols-2 gap-2">
                                    {(() => {
                                      if (goalData.exam_type.includes('IGCSE')) return ['Y10', 'Y11', 'Intensive'];
                                      if (goalData.exam_type === 'IB') return ['Year 1', 'Year 2', 'Intensive'];
                                      if (goalData.exam_type.includes('A-Level')) return ['AS', 'A2', 'Retake', 'Intensive'];
                                      if (goalData.exam_type.includes('DSE')) return ['F4', 'F5', 'F6', 'Intensive'];
                                      return [];
                                    })().map((phase) => (
                                      <button
                                        key={phase}
                                        type="button"
                                        onClick={() => setGoalData(prev => ({ ...prev, exam_year_phase: phase }))}
                                        className={`p-2 rounded-md text-sm font-medium transition-all ${
                                          goalData.exam_year_phase === phase
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                        }`}
                                      >
                                        {phase}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Business/HSKK Notice */}
                      <div className="mt-4 p-3 bg-blue-900 bg-opacity-20 border border-blue-700 rounded-lg">
                        <div className="flex items-start">
                          <AlertCircle size={16} className="text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="text-blue-200 text-xs">
                            <strong>Looking for Business Mandarin or HSKK?</strong> For specialized business Mandarin or HSKK speaking test prep, consider our{' '}
                            <a 
                              href="https://www.mandarintutorhk.com/services" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-300 hover:text-blue-200 underline"
                            >
                              private tutoring sessions
                            </a>
                            {' '}for more focused learning.
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {isCantonese && (
                    <>
                      <p className="text-sm text-gray-400 mb-3">What level are you?</p>
                      {[
                        { value: 'beginner', label: 'Beginner' },
                        { value: 'intermediate', label: 'Intermediate' },
                        { value: 'advanced', label: 'Advanced' }
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                            goalData.cantonese_level === option.value
                              ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                              : 'border-gray-600 hover:border-gray-500'
                          }`}
                        >
                          <input
                            type="radio"
                            name="cantoneseLevel"
                            value={option.value}
                            checked={goalData.cantonese_level === option.value}
                            onChange={(e) => setGoalData(prev => ({ ...prev, cantonese_level: e.target.value }))}
                            className="sr-only"
                          />
                          <span className="flex-1 text-sm font-medium">{option.label}</span>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            goalData.cantonese_level === option.value ? 'border-purple-500' : 'border-gray-500'
                          }`}>
                            {goalData.cantonese_level === option.value && (
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            )}
                          </div>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Goals Section - Show for tutoring trials */}
          {isTutoringTrial && !loadingClassType && (
            <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-4 py-3 border-b border-gray-700">
                <h3 className="font-semibold text-base flex items-center">
                  <Target size={16} className="mr-2" />
                  What's your goal?
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { value: 'hsk', label: 'HSK Test Preparation' },
                  { value: 'hskk', label: 'HSKK (Speaking Test)' },
                  { value: 'business', label: 'Business Mandarin' },
                  { value: 'exam', label: 'School Exam Support' },
                  { value: 'conversational', label: 'Light Conversational' }
                ].map((option) => (
                  <div key={option.value}>
                    <label
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                        goalData.goal === option.value
                          ? 'border-purple-500 bg-purple-500 bg-opacity-10'
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <input
                        type="radio"
                        name="goal"
                        value={option.value}
                        checked={goalData.goal === option.value}
                        onChange={(e) => setGoalData(prev => ({ 
                          ...prev, 
                          goal: e.target.value,
                          goal_level: '',
                          exam_type: '',
                          exam_level: '',
                          exam_year_phase: ''
                        }))}
                        className="sr-only"
                      />
                      <span className="flex-1 text-sm font-medium">{option.label}</span>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        goalData.goal === option.value ? 'border-purple-500' : 'border-gray-500'
                      }`}>
                        {goalData.goal === option.value && (
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        )}
                      </div>
                    </label>

                    {/* HSK Level Selection */}
                    {goalData.goal === 'hsk' && option.value === 'hsk' && (
                      <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          Which HSK level are you targeting?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {[1, 2, 3, 4, 5, 6].map((level) => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => setGoalData(prev => ({ ...prev, goal_level: level.toString() }))}
                              className={`p-2 rounded-md text-sm font-medium transition-all ${
                                goalData.goal_level === level.toString()
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              HSK {level}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* HSKK Level Selection */}
                    {goalData.goal === 'hskk' && option.value === 'hskk' && (
                      <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          Which HSKK level are you targeting?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => setGoalData(prev => ({ ...prev, goal_level: level }))}
                              className={`p-2 rounded-md text-sm font-medium transition-all ${
                                goalData.goal_level === level
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Conversational Level Selection */}
                    {goalData.goal === 'conversational' && option.value === 'conversational' && (
                      <div className="mt-3 ml-4 p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          What level are you?
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                          {['Beginner', 'Intermediate', 'Advanced'].map((level) => (
                            <button
                              key={level}
                              type="button"
                              onClick={() => setGoalData(prev => ({ ...prev, goal_level: level }))}
                              className={`p-2 rounded-md text-sm font-medium transition-all ${
                                goalData.goal_level === level
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              {level}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Exam Details */}
                    {goalData.goal === 'exam' && option.value === 'exam' && (
                      <div className="mt-3 ml-4 space-y-4">
                        <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                          <label className="block text-sm font-medium text-gray-300 mb-3">
                            Which exam are you preparing for?
                          </label>
                          <select
                            value={goalData.exam_type}
                            onChange={(e) => setGoalData(prev => ({
                              ...prev,
                              exam_type: e.target.value,
                              exam_level: '',
                              exam_year_phase: ''
                            }))}
                            className="w-full p-2 bg-gray-600 border border-gray-500 rounded-md text-white text-sm"
                          >
                            <option value="">Select exam...</option>
                            {['IGCSE 0523', 'IGCSE 0547', 'IB', 'A-Level', 'DSE'].map((exam) => (
                              <option key={exam} value={exam}>{exam}</option>
                            ))}
                          </select>
                        </div>

                        {goalData.exam_type === 'IB' && (
                          <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                              IB Level
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {['IB SL', 'IB HL'].map((level) => (
                                <button
                                  key={level}
                                  type="button"
                                  onClick={() => setGoalData(prev => ({ ...prev, exam_level: level }))}
                                  className={`p-2 rounded-md text-sm font-medium transition-all ${
                                    goalData.exam_level === level
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                  }`}
                                >
                                  {level}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {goalData.exam_type && (goalData.exam_type !== 'IB' || goalData.exam_level) && (
                          <div className="p-4 bg-gray-700 bg-opacity-50 rounded-lg border border-gray-600">
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                              Year/Phase
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                              {(() => {
                                if (goalData.exam_type.includes('IGCSE')) return ['Y10', 'Y11', 'Intensive'];
                                if (goalData.exam_type === 'IB') return ['Year 1', 'Year 2', 'Intensive'];
                                if (goalData.exam_type.includes('A-Level')) return ['AS', 'A2', 'Retake', 'Intensive'];
                                if (goalData.exam_type.includes('DSE')) return ['F4', 'F5', 'F6', 'Intensive'];
                                return [];
                              })().map((phase) => (
                                <button
                                  key={phase}
                                  type="button"
                                  onClick={() => setGoalData(prev => ({ ...prev, exam_year_phase: phase }))}
                                  className={`p-2 rounded-md text-sm font-medium transition-all ${
                                    goalData.exam_year_phase === phase
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                                  }`}
                                >
                                  {phase}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Contact information form */}
          <form onSubmit={handleSubmit}>
            <div className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-800 to-purple-700 px-6 py-3 border-b border-gray-700">
                <h3 className="font-semibold text-base flex items-center">
                  <Mail size={16} className="mr-2" />
                  How can we reach you?
                </h3>
              </div>
              
              <div className="p-6 space-y-5">
                <div className="space-y-4">
                  <Input
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    className="border-gray-600 focus:ring-purple-500 focus:border-transparent"
                  />
                  
                  <Input
                    label="Email Address"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    helperText={isTutoringTrial 
                      ? "We'll email you to confirm your trial lesson time." 
                      : "We'll email you when someone matches with your available times."}
                    required
                    className="border-gray-600 focus:ring-purple-500 focus:border-transparent"
                  />
                  
                  <Input
                    label="Phone Number (for SMS/WhatsApp)"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+852 1234-5678"
                    required
                    className="border-gray-600 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex justify-between pt-4 mt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                    className="bg-gray-700 hover:bg-gray-600 border border-gray-600"
                  >
                    Back
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    isLoading={isSubmitting}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StartConfirmationPage;